import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, Copy, Check, Mail } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { registerCouple, signIn, resetPassword } from '../services/authService';

const CoupleAuth = () => {
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [showCoupleCodeModal, setShowCoupleCodeModal] = useState(false);
  const [generatedCoupleCode, setGeneratedCoupleCode] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Password Reset State
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState('');
  const [resetError, setResetError] = useState('');
  
  // Form data
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    counselorCode: '',
    partnerNumber: 1
  });

  // Redirect if already logged in
  useEffect(() => {
    if (currentUser && userRole && loginSuccess) {
      navigate('/couple/dashboard');
    }
  }, [currentUser, userRole, loginSuccess, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      throw new Error('Please enter your full name');
    }
    if (!formData.email.trim()) {
      throw new Error('Please enter your email');
    }
    if (!formData.counselorCode.trim()) {
      throw new Error('Please enter a registration code');
    }
    if (formData.password.length < 6) {
      throw new Error('Password must be at least 6 characters');
    }
    if (formData.password !== formData.confirmPassword) {
      throw new Error('Passwords do not match');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      validateForm();
      
      console.log('=== COUPLE REGISTRATION ===');
      console.log('Partner number:', formData.partnerNumber);
      console.log('Code entered:', formData.counselorCode);
      
      const result = await registerCouple(
        formData.email.trim(),
        formData.password.trim(),
        formData.name.trim(),
        formData.counselorCode.trim().toUpperCase(),
        parseInt(formData.partnerNumber)
      );
      
      console.log('Registration result:', result);
      
      if (result.error || !result.user) {
        throw new Error(result.error || 'Registration failed');
      }
      
      if (parseInt(formData.partnerNumber) === 1) {
        if (!result.coupleCode) {
          console.error('❌ No couple code returned!');
          throw new Error('Failed to generate couple code. Please try again or contact support.');
        }
        
        console.log('✅ Partner 1 registered with couple code:', result.coupleCode);
        setGeneratedCoupleCode(result.coupleCode);
        setShowCoupleCodeModal(true);
        
      } else {
        console.log('✅ Partner 2 registered and linked');
        
        setSuccess(`
          ✅ Account created successfully!
          
          You have been linked to your partner.
          
          Your couple is now pending counselor approval.
          You'll be notified once approved.
          
          Redirecting to dashboard...
        `);
        
        setTimeout(() => {
          setLoginSuccess(true);
        }, 3000);
      }
      
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('=== LOGIN ATTEMPT ===');
      console.log('Email:', formData.email);
      
      const result = await signIn(
        formData.email.trim(), 
        formData.password.trim()
      );
      
      console.log('Login result:', result);
      
      if (result.error || !result.user) {
        throw new Error(result.error || 'Login failed');
      }
      
      console.log('✅ Login successful');
      setLoginSuccess(true);
      
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Password Reset Handler
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess('');
    setResetLoading(true);

    try {
      if (!resetEmail.trim()) {
        throw new Error('Please enter your email address');
      }

      const result = await resetPassword(resetEmail.trim());

      if (result.error) {
        throw new Error(result.error);
      }

      setResetSuccess('Password reset email sent! Check your inbox (and spam folder) for the reset link.');
      setResetEmail('');
      
      // Auto-close after 5 seconds
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetSuccess('');
      }, 5000);

    } catch (err) {
      setResetError(err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedCoupleCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeCoupleCodeModal = () => {
    setShowCoupleCodeModal(false);
    setLoginSuccess(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="inline-block p-3 bg-purple-100 rounded-full mb-4">
              <Heart className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold mb-2">
              {isLogin ? 'Couple Login' : 'Couple Registration'}
            </h1>
            <p className="text-gray-600">
              {isLogin 
                ? 'Sign in to continue your assessment' 
                : 'Begin your pre-marriage assessment'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm whitespace-pre-line">
              {success}
            </div>
          )}

          <form onSubmit={isLogin ? handleLogin : handleRegister} className="space-y-4">
            {!isLogin && (
              <>
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your full name"
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>
              </>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Password <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Minimum 6 characters"
                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                required
                minLength="6"
              />
              {!isLogin && (
                <p className="text-xs text-gray-500 mt-1">Minimum 6 characters</p>
              )}
            </div>

            {/* Forgot Password Link - Only show on login */}
            {isLogin && (
              <div className="text-right">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(true);
                    setResetEmail(formData.email);
                  }}
                  className="text-sm text-purple-600 hover:text-purple-700 hover:underline"
                >
                  Forgot Password?
                </button>
              </div>
            )}

            {!isLogin && (
              <>
                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Confirm Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Re-enter password"
                    className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                {/* Registration Code */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Registration Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="counselorCode"
                    value={formData.counselorCode}
                    onChange={handleChange}
                    placeholder="ABC123 OR ABC123-C001"
                    maxLength={15}
                    className="w-full p-3 border rounded-lg uppercase focus:ring-2 focus:ring-purple-500"
                    required
                  />
                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                    <p><strong>If you're registering FIRST:</strong> Enter your counselor's 6-character code (e.g., ABC123)</p>
                    <p><strong>If your partner registered FIRST:</strong> Enter the couple code they received (e.g., ABC123-C001)</p>
                  </div>
                </div>

                {/* Partner Number Selection */}
                <div>
                  <label className="block text-sm font-medium mb-2">
                    I am Partner <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="partnerNumber"
                        value="1"
                        checked={formData.partnerNumber === 1}
                        onChange={() => setFormData({ ...formData, partnerNumber: 1 })}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg text-center transition ${
                        formData.partnerNumber === 1 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-300'
                      }`}>
                        <div className="font-semibold">Partner 1</div>
                        <div className="text-xs text-gray-500 mt-1">Registering first</div>
                      </div>
                    </label>

                    <label className="cursor-pointer">
                      <input
                        type="radio"
                        name="partnerNumber"
                        value="2"
                        checked={formData.partnerNumber === 2}
                        onChange={() => setFormData({ ...formData, partnerNumber: 2 })}
                        className="sr-only"
                      />
                      <div className={`p-4 border-2 rounded-lg text-center transition ${
                        formData.partnerNumber === 2 
                          ? 'border-purple-500 bg-purple-50' 
                          : 'border-gray-200 hover:border-purple-300'
                      }`}>
                        <div className="font-semibold">Partner 2</div>
                        <div className="text-xs text-gray-500 mt-1">Partner already registered</div>
                      </div>
                    </label>
                  </div>
                  <p className="text-xs text-gray-600 mt-2">
                    💡 Select "Partner 1" if you're registering first, or "Partner 2" if your partner already registered.
                  </p>
                </div>
              </>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 font-semibold transition"
            >
              {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsLogin(!isLogin);
                setError('');
                setSuccess('');
              }}
              className="text-purple-600 hover:text-purple-700 text-sm"
            >
              {isLogin 
                ? "Don't have an account? Register here" 
                : 'Already have an account? Sign in'}
            </button>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <div className="inline-block p-3 bg-blue-100 rounded-full mb-4">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Reset Password</h2>
              <p className="text-gray-600 text-sm">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            {resetError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {resetError}
              </div>
            )}

            {resetSuccess && (
              <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                {resetSuccess}
              </div>
            )}

            <form onSubmit={handlePasswordReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="your.email@example.com"
                  className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={resetLoading}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-semibold transition"
              >
                {resetLoading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <button
              onClick={() => {
                setShowForgotPassword(false);
                setResetError('');
                setResetSuccess('');
              }}
              className="w-full mt-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
            >
              Back to Login
            </button>
          </div>
        </div>
      )}

      {/* Couple Code Modal - Shows after Partner 1 registers */}
      {showCoupleCodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
            <div className="text-center mb-6">
              <div className="inline-block p-3 bg-green-100 rounded-full mb-4">
                <Check className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold mb-2">Registration Successful!</h2>
            </div>
            
            <div className="bg-green-50 border-2 border-green-500 rounded-lg p-6 mb-6">
              <p className="text-sm font-semibold text-gray-700 mb-2 text-center">
                YOUR COUPLE CODE
              </p>
              <div className="bg-white rounded-lg p-4 mb-4">
                <p className="text-3xl font-bold text-center text-green-700 font-mono tracking-wider">
                  {generatedCoupleCode}
                </p>
              </div>
              
              <button
                onClick={copyToClipboard}
                className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2"
              >
                {copied ? (
                  <>
                    <Check className="w-5 h-5" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-5 h-5" />
                    Copy Code
                  </>
                )}
              </button>
            </div>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-6">
              <p className="font-semibold text-yellow-800 mb-2">⚠️ Important:</p>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• Save this code - you'll need it</li>
                <li>• Share it with your partner</li>
                <li>• Your partner will use this code to register</li>
              </ul>
            </div>
            
            <div className="text-sm text-gray-600 mb-6">
              <p className="mb-2"><strong>Next Steps:</strong></p>
              <ol className="list-decimal list-inside space-y-1">
                <li>Give this code to your partner</li>
                <li>They register and select "Partner 2"</li>
                <li>They enter this code during registration</li>
                <li>Wait for counselor approval</li>
              </ol>
            </div>
            
            <button
              onClick={closeCoupleCodeModal}
              className="w-full bg-purple-600 text-white py-3 rounded-lg hover:bg-purple-700 transition font-semibold"
            >
              Continue to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoupleAuth;